import React, { useState, useRef } from 'react';
import { HeartIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import AuthenticatedImage from './AuthenticatedImage';
import HeartAnimation from './HeartAnimation';
import { StyleMatcherDress } from '../services/styleMatcher';

interface StyleMatcherCardProps {
  dress: StyleMatcherDress;
  onLike: (dressId: string) => void;
  onPass?: (dressId: string) => void;
  isLiked?: boolean;
}

const StyleMatcherCard: React.FC<StyleMatcherCardProps> = ({ 
  dress, 
  onLike, 
  onPass,
  isLiked = false 
}) => {
  const [showHeartAnimation, setShowHeartAnimation] = useState(false);
  const [liked, setLiked] = useState(isLiked);
  const lastTapRef = useRef<number>(0);
  const cardRef = useRef<HTMLDivElement>(null);

  // Handle double tap/click
  const handleDoubleTap = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    
    const currentTime = Date.now();
    const tapDelay = currentTime - lastTapRef.current;
    
    if (tapDelay < 300 && tapDelay > 0) {
      // Double tap detected
      handleLike();
    }
    
    lastTapRef.current = currentTime;
  };

  const handleLike = () => {
    if (!liked) {
      setLiked(true);
      setShowHeartAnimation(true);
      onLike(dress.id);
    }
  };

  const handleHeartClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleLike();
  };

  return (
    <div 
      ref={cardRef}
      className="relative bg-white rounded-lg shadow-sm overflow-hidden no-tap-zoom"
      onClick={handleDoubleTap}
      onTouchEnd={handleDoubleTap}
    >
      {/* Main Image */}
      <div className="relative aspect-[3/4] bg-gray-100">
        <AuthenticatedImage
          src={dress.image}
          alt={dress.name}
          className="w-full h-full object-cover"
        />
        
        {/* Heart Icon */}
        <button
          onClick={handleHeartClick}
          className="absolute top-4 right-4 p-2 bg-white/80 backdrop-blur-sm rounded-full shadow-md hover:bg-white transition-colors"
          aria-label={liked ? "Unlike dress" : "Like dress"}
        >
          {liked ? (
            <HeartIconSolid className="w-6 h-6 text-red-500" />
          ) : (
            <HeartIcon className="w-6 h-6 text-gray-700" />
          )}
        </button>
        
        {/* Heart Animation */}
        {showHeartAnimation && (
          <HeartAnimation onComplete={() => setShowHeartAnimation(false)} />
        )}
      </div>
      
      {/* Dress Info */}
      <div className="p-4">
        <h3 className="font-semibold text-lg text-gray-900">{dress.name}</h3>
        {dress.designer && (
          <p className="text-sm text-gray-600 mt-1">by {dress.designer}</p>
        )}
        {dress.categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {dress.categories.map((category) => (
              <span
                key={category}
                className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full"
              >
                {category}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StyleMatcherCard;